import type { GmAction, SideBrandingSet } from '@badger-sentinel/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { gmControl } from '../api/games.js';
import { ProtocolMismatchScreen, SupersededScreen } from './chrome.js';
import { ConsoleShell } from '../console/ConsoleShell.js';
import { guardedReport, seatRefOf, useGuardedView } from '../console/state/guard.js';
import { useConsoleStore } from '../console/state/store.js';
import { useServerNow } from '../console/useServerNow.js';
import { useBsTheme } from '../theme/useTheme.js';
import { useSeatSocket } from '../ws/useSeatSocket.js';
import { useIntelArchive } from '../ws/useIntelArchive.js';

/**
 * The seat container — the one place in a seat's page that does I/O.
 *
 * **This file is `GameHarness` with the harness taken out** (Phase 9). The M2
 * harness was a container with a debug widget bolted underneath it, and every
 * phase since has removed one piece of the widget: Phase 3 the order pad, Phase 4
 * the missile-warning board, Phase 8 the intel list, and this phase the picture
 * tables and the `children` escape hatch on `ConsoleShell` that carried them. What
 * is left is not a smaller harness; it is the container, and the rename says so.
 *
 * ## What a container is for, here
 *
 * `ConsoleShell` is presentational (M5-D2) — every prop is a protocol type or a
 * thin derivation of one, and it emits callbacks. That is what makes the whole
 * frame renderable from a fixture, and it is only true because the four things a
 * console must never hold live in this file instead:
 *
 *  - the **socket** (`useSeatSocket`) and the corrected **clock** (`useServerNow`),
 *  - the **seat token**, which the archive's REST read needs and the shell must
 *    never see,
 *  - the **viewer guard**, applied to both documents the projection stamps,
 *  - the **theme**, which reaches `document.documentElement` and so belongs to the
 *    app rather than to any one screen.
 *
 * Nothing below is new. Every hook here was in `GameHarness` on the day before
 * this phase, in the same order, doing the same thing — which was the point of
 * retiring the file this way rather than writing a replacement and moving pieces
 * into it one at a time.
 */

export interface SeatConsoleProps {
  readonly baseUrl: string;
  readonly wsUrl: string;
  readonly gameId: string;
  readonly seatToken: string;
  /** From `ClaimedSeat` — the only route these have to a reloaded seat. */
  readonly scenario: string;
  readonly sides: SideBrandingSet;
  readonly onLeave: () => void;
}

export function SeatConsole(props: SeatConsoleProps): JSX.Element {
  const socket = useSeatSocket(props.wsUrl, props.seatToken);
  const theme = useBsTheme();
  const serverNowMs = useServerNow(socket.clockOffsetMs);
  const [gmBusy, setGmBusy] = useState(false);

  const setSeat = useConsoleStore((state) => state.setSeat);
  const showToast = useConsoleStore((state) => state.showToast);

  const seat = socket.seat;
  const isGm = seat?.isGm ?? false;
  const turn = socket.phase?.turn ?? 1;

  // The store's seat is the guard's authority, and it comes from `session.hello`
  // — never from a view, which is the thing being checked.
  useEffect(() => setSeat(seatRefOf(seat)), [seat, setSeat]);

  const view = useGuardedView(socket.view);

  /**
   * The intel report is the **other** document the projection stamps a viewer on,
   * so it goes through the same guard as the snapshot (shell.md must-not #2).
   * Without it a report replayed by `resume`, or one that outlived a seat switch,
   * would put another seat's aliases in front of this one — and the aliases are
   * the whole scheme.
   */
  const intel = guardedReport(socket.intel, seatRefOf(seat));

  /**
   * The archive is the console's one REST read of game data, and it is off for the
   * GM seat — whose token the route refuses on purpose (403).
   *
   * **Phase 9 checked what this flag was actually doing, and it is the second
   * lock rather than the first.** A GM seat is served `gm.snapshot`, never
   * `view.snapshot` (`broadcaster.sendGmSnapshot`), so `socket.view` stays null,
   * `capabilities` stays null, the tab strip is empty and `IntelView` never
   * mounts — nothing ever calls `request`. The flag is kept because it states the
   * refusal at the layer that would have to make the call, and because the day a
   * GM console does get a picture is the day this would otherwise start firing
   * sixteen requests to be told no.
   */
  const archive = useIntelArchive(props.baseUrl, props.gameId, props.seatToken, !isGm);

  const orderable = useMemo(() => (view ? view.friendly.filter((a) => a.orderable) : []), [view]);

  // Toasts confirm the *round trip*, so they are driven by the state the server
  // sent back rather than by the click: a commit that never reached the server
  // must not produce the same feedback as one that did.
  const submitted = socket.orders?.submitted ?? null;
  const previousSubmitted = useRef<boolean | null>(null);
  useEffect(() => {
    const previous = previousSubmitted.current;
    if (submitted !== null && previous !== null && submitted !== previous) {
      showToast(submitted ? 'ORDERS SUBMITTED' : 'ORDERS WITHDRAWN', submitted ? 'ok' : 'warn');
    }
    previousSubmitted.current = submitted;
  }, [submitted, showToast]);

  const resume = socket.resume;
  useEffect(() => {
    if (resume === null) return;
    /*
     * Both arms say the same operational thing — your picture is current — and the
     * difference between them is transport bookkeeping. `2 FRAMES REPLAYED` was the
     * first string a cadet read in this product, six seconds over the top of the map,
     * and a frame count is not something anyone acts on.
     */
    showToast('RESUMED · PICTURE CURRENT');
  }, [resume, showToast]);

  const onGmControl = useCallback(
    (action: GmAction, extendMs?: number) => {
      setGmBusy(true);
      void gmControl(props.baseUrl, props.gameId, props.seatToken, action, extendMs).finally(() =>
        setGmBusy(false),
      );
    },
    [props.baseUrl, props.gameId, props.seatToken],
  );

  // Both terminal states leave the console rather than sitting inside it. The
  // status bar can name them; it must not be the only thing saying so while
  // live-looking chrome surrounds a seat that is gone.
  if (socket.status === 'protocol-mismatch') {
    return (
      <ProtocolMismatchScreen
        detail={
          (socket.terminalReason ?? 'The server was redeployed underneath this page.') +
          ' Reload to pick up a matching client — the game code and passphrase still work.'
        }
      />
    );
  }
  if (socket.status === 'superseded') {
    return (
      <SupersededScreen
        reason={
          socket.terminalReason ??
          'This seat has been claimed by another device or released by the GM.'
        }
        onExit={props.onLeave}
      />
    );
  }

  if (seat === null) {
    return (
      <div
        data-bs-theme={theme.theme}
        data-testid="attaching"
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg)',
          color: 'var(--text-muted)',
          fontFamily: 'var(--font-data)',
          fontSize: 11,
          letterSpacing: '.14em',
        }}
      >
        ATTACHING
      </div>
    );
  }

  return (
    <ConsoleShell
      seat={seat}
      sides={props.sides}
      scenario={props.scenario}
      phase={socket.phase}
      serverNowMs={serverNowMs}
      connection={socket.status}
      roundTripMs={socket.roundTripMs}
      escalation={
        view === null
          ? null
          : { counter: view.theatre.escalationCounter, level: view.theatre.escalationLevel }
      }
      capabilities={
        view === null
          ? null
          : { readOnly: view.capabilities.readOnly, tabs: view.capabilities.tabs }
      }
      view={view}
      intel={intel}
      intelArchive={archive.reports}
      onRequestIntel={archive.request}
      orderableCount={view === null ? null : orderable.length}
      orders={socket.orders}
      team={socket.team}
      errors={socket.errors}
      gmMessages={socket.gmMessages}
      mwStages={socket.mwStages}
      mwReleases={socket.mwReleases}
      theme={theme.theme}
      onToggleTheme={theme.toggle}
      onLeave={props.onLeave}
      onSubmit={(next) => socket.sendSubmit(turn, next)}
      onDraft={(draftTurn, orders) => socket.sendDraft(draftTurn, orders)}
      onMwAction={socket.mwAction}
      onPublish={socket.publish}
      gm={isGm ? { busy: gmBusy, onControl: onGmControl } : null}
    />
  );
}
